import { useMemo, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, Dialog, IconButton, TextField, Typography } from "@mui/material";

export interface RenameEntityDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly title: string;
  /** Defaults to `rename-entity-dialog-title`. */
  readonly titleId?: string;
  readonly isSaving?: boolean;
  readonly allowUnchangedSave?: boolean;
  readonly primaryLabel: string;
  readonly initialPrimary: string;
  readonly primaryHelperTextWhenEmpty?: string;
  /** When set, a second field is shown (e.g. scene id for storyboard shots). */
  readonly secondaryLabel?: string;
  readonly initialSecondary?: string;
  readonly secondaryHelperText?: string;
  readonly secondaryHelperTextWhenEmpty?: string;
  readonly onSave: (values: { primary: string; secondary?: string }) => Promise<void> | void;
}

export const RenameEntityDialog = ({
  open,
  onClose,
  title,
  titleId = "rename-entity-dialog-title",
  isSaving = false,
  allowUnchangedSave = false,
  primaryLabel,
  initialPrimary,
  primaryHelperTextWhenEmpty = "This field is required.",
  secondaryLabel,
  initialSecondary = "",
  secondaryHelperText,
  secondaryHelperTextWhenEmpty = "This field is required.",
  onSave
}: RenameEntityDialogProps) => {
  const [primary, setPrimary] = useState(initialPrimary);
  const [secondary, setSecondary] = useState(initialSecondary);
  const [touchedPrimary, setTouchedPrimary] = useState(false);
  const [touchedSecondary, setTouchedSecondary] = useState(false);

  const hasSecondary = Boolean(secondaryLabel);

  const handleEnter = () => {
    setPrimary(initialPrimary);
    setSecondary(initialSecondary);
    setTouchedPrimary(false);
    setTouchedSecondary(false);
  };

  const trimmedPrimary = useMemo(() => primary.trim(), [primary]);
  const trimmedSecondary = useMemo(() => secondary.trim(), [secondary]);
  const trimmedInitialPrimary = useMemo(() => initialPrimary.trim(), [initialPrimary]);
  const trimmedInitialSecondary = useMemo(() => initialSecondary.trim(), [initialSecondary]);

  const primaryError = touchedPrimary && trimmedPrimary.length === 0;
  const secondaryError = hasSecondary && touchedSecondary && trimmedSecondary.length === 0;

  const valuesChanged = trimmedPrimary !== trimmedInitialPrimary || (hasSecondary && trimmedSecondary !== trimmedInitialSecondary);

  const canSave = !isSaving && trimmedPrimary.length > 0 && (!hasSecondary || trimmedSecondary.length > 0) && (allowUnchangedSave || valuesChanged);

  const handleSave = async () => {
    setTouchedPrimary(true);
    if (hasSecondary) {
      setTouchedSecondary(true);
    }

    if (!canSave) {
      return;
    }

    await onSave(hasSecondary ? { primary: trimmedPrimary, secondary: trimmedSecondary } : { primary: trimmedPrimary });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby={titleId}
      onTransitionEnter={handleEnter}
      slotProps={{
        paper: {
          elevation: 0,
          sx: {
            width: 520,
            maxWidth: "calc(100vw - 48px)",
            borderRadius: "12px"
          }
        }
      }}
    >
      <Box
        sx={{
          height: 56,
          px: 3,
          py: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottomWidth: 1,
          borderBottomStyle: "solid",
          borderBottomColor: "divider"
        }}
      >
        <Typography id={titleId} sx={{ fontSize: 16, fontWeight: 500, letterSpacing: "0.15px" }}>
          {title}
        </Typography>
        <IconButton onClick={onClose} sx={{ p: 1, borderRadius: 999 }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Box sx={{ px: 3, pt: 3, pb: 3 }}>
        <Box sx={{ mb: hasSecondary ? 2 : 3 }}>
          <TextField
            autoFocus
            fullWidth
            variant="standard"
            label={primaryLabel}
            value={primary}
            onChange={(e) => setPrimary(e.target.value)}
            onBlur={() => setTouchedPrimary(true)}
            error={primaryError}
            helperText={primaryError ? primaryHelperTextWhenEmpty : " "}
            slotProps={{
              formHelperText: { sx: { m: 0, mt: 0.5, minHeight: 16 } },
              inputLabel: { shrink: true }
            }}
          />
        </Box>
        {hasSecondary ? (
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              variant="standard"
              label={secondaryLabel}
              value={secondary}
              onChange={(e) => setSecondary(e.target.value)}
              onBlur={() => setTouchedSecondary(true)}
              error={secondaryError}
              helperText={secondaryError ? secondaryHelperTextWhenEmpty : secondaryHelperText || " "}
              slotProps={{
                formHelperText: { sx: { m: 0, mt: 0.5, minHeight: 16 } },
                inputLabel: { shrink: true }
              }}
            />
          </Box>
        ) : null}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
          <Button
            variant="text"
            onClick={onClose}
            disabled={isSaving}
            sx={{ textTransform: "uppercase", letterSpacing: "0.4px", fontSize: 14, fontWeight: 500 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              void handleSave();
            }}
            disabled={!canSave}
            sx={{ textTransform: "uppercase", letterSpacing: "0.4px", fontSize: 14, fontWeight: 500 }}
          >
            Save
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

interface RenameProjectDialogProps {
  readonly open: boolean;
  readonly initialName: string;
  readonly isSaving?: boolean;
  readonly allowUnchangedSave?: boolean;
  readonly onClose: () => void;
  readonly onSave: (nextName: string) => Promise<void> | void;
}

const RenameProjectDialog = ({ open, initialName, isSaving = false, allowUnchangedSave = false, onClose, onSave }: RenameProjectDialogProps) => (
  <RenameEntityDialog
    open={open}
    onClose={onClose}
    title="Project name"
    titleId="rename-project-dialog-title"
    isSaving={isSaving}
    allowUnchangedSave={allowUnchangedSave}
    primaryLabel="Project Name"
    initialPrimary={initialName}
    primaryHelperTextWhenEmpty="Project name is required."
    onSave={async (v) => {
      await onSave(v.primary);
    }}
  />
);

export default RenameProjectDialog;
